import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Grid, Toolbar, Typography, Stack, useMediaQuery } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';

// types
import { openDrawer, showAppAlert, setAppUser } from 'store/reducers/appreducer';
import SwitchDevice from 'components/SwitchDevice';
import AddDevice from 'components/AddDevice';
import { AuthContext } from 'auth/auth-context';
import MainCard from 'components/MainCard';
import useOrganizationValidation from 'hooks/useOrganizationValidation';


// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const { organizationName } = useParams()
  useOrganizationValidation()
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { isSignedIn, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  const { drawerOpen, openItemTitle, selectedDevice } = useSelector((state) => state.appreducer);

  // drawer toggler
  const [open, setOpen] = useState(drawerOpen);

  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  // Set appUser from sessionStorage for partner sessions (since SwitchDevice doesn't render for partners)
  useEffect(() => {
    // Check for partner session inside useEffect to ensure fresh value
    const isPartnerSession = typeof window !== 'undefined' && sessionStorage.getItem('is_partner_session') === 'true';
    
    if (isPartnerSession) {
      const userDataStr = sessionStorage.getItem('user');
      if (userDataStr) {
        try {
          const partnerUser = JSON.parse(userDataStr);
          dispatch(setAppUser({ appUser: partnerUser }));
        } catch (e) {
          console.error('Failed to parse partner user data from sessionStorage:', e);
        }
      }
    }
  }, [dispatch]);

  // check for partner session (for conditional rendering)
  const isPartnerSession = typeof window !== 'undefined' && sessionStorage.getItem('is_partner_session') === 'true';

  useEffect(() => {
    // Skip auth check for deeplink routes - let them handle their own auth
    if (window.location.pathname.includes('integrations/deeplink')) {
      return;
    }
    
    if (!isSignedIn && !loading) {
      navigate(`/${organizationName}/login`);
      // console.log('not signed in');
    }
  }, [isSignedIn, loading]);

  // set media wise responsive drawer
  useEffect(() => {
    setOpen(!matchDownMD);
    dispatch(openDrawer({ drawerOpen: !matchDownMD }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownMD]);

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  // If deeplink route, render only the child outlet (no shell)
  const isDeeplinkRoute = typeof window !== 'undefined' && window.location.pathname.includes('integrations/deeplink');
  if (isDeeplinkRoute) {
    return (
      <Box component="main" sx={{ width: '100%', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Outlet />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Header open={open} handleDrawerToggle={handleDrawerToggle} />
      <Drawer open={open} handleDrawerToggle={handleDrawerToggle} organizationName={organizationName}/>
      <Box component="main" sx={{ width: '100%', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Toolbar />
        {/* <Breadcrumbs navigation={navigation} title /> */}
        <Grid container rowSpacing={2.75} columnSpacing={2.75}>
          {/* row 1 */}
          <Grid item xs={12} md={12} lg={12} sx={{ mb: 0 }}>
            <Box sx={{ mt: -1 }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                  <Typography variant="h5">{openItemTitle}</Typography>
                </Grid>
                <Grid item>
                  <Stack direction="row" alignItems="center" spacing={0}>
                    {!isPartnerSession && <SwitchDevice />}
                    {!isPartnerSession && <AddDevice />}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          {
            selectedDevice === '' ? (
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <MainCard contentSX={{ p: 0, minHeight: 100 }} contentStyle={{ paddingBottom: '0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    No device added. Please add the device.
                  </Box>
                </MainCard>
              </Grid>
            ) : (
              <>
                <Outlet />
              </>
            )
          }
        </Grid>
      </Box>
    </Box>
  );
};

export default MainLayout;
